import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import BenchlingConfig from 'BenchlingConfig';
import staticURL from 'lib/staticURL';
import getTestProps from 'test/lib/cypress/getTestProps';

const generateChartId = (): string => `secure-plotly-chart-${uuidv4()}`;

const getPostMessageOrigin = (): string =>
  BenchlingConfig.CDN_DOMAIN ? `https://${BenchlingConfig.CDN_DOMAIN}` : '/';

export interface SecurePlotlyChartProps {
  libraryConfig: unknown;
  /**
   * Called when a chart is unloaded, due to browser resource limitations.
   *
   * Charts that use WebGL may be unloaded if the browser reaches its maximum number of WebGL contexts.
   */
  onChartForceUnloaded?: () => void;
}

/**
 * Securely render untrusted chart content.
 *
 * We use an iFrame to isolate Plotly chart rendering with
 * tight CSP to prevent data exfiltration and XSS attacks.
 *
 * This component renders chart data securely by passing it to an iframe
 * hosted on the CDN using postMessage. Analogous to PlotlyChart but with
 * additional security isolation.
 *
 * @package
 */
export function SecurePlotlyChart({
  libraryConfig,
  onChartForceUnloaded,
}: SecurePlotlyChartProps): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [iframeURL, setIframeURL] = useState('about:blank');
  const chartIdRef = useRef<string>(generateChartId());

  useEffect(() => {
    // Only fetch the iframe URL once, this prevents the error log from firing too many times.
    const baseURL = staticURL('/static/charts/plotly-content.html');
    const urlWithChartIdAndOrigin = `${baseURL}?chartId=${encodeURIComponent(chartIdRef.current)}&origin=${encodeURIComponent(window.location.origin)}`;
    setIframeURL(urlWithChartIdAndOrigin);
  }, []);

  // Listen for iframe ready message
  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      // Only handle messages from our specific iframe
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data.type === 'CHART_IFRAME_READY' && event.data.chartId === chartIdRef.current) {
        setIsIframeReady(true);
      } else if (
        event.data.type === 'CHART_WEBGL_CONTEXT_LOST' &&
        event.data.chartId === chartIdRef.current
      ) {
        onChartForceUnloaded?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onChartForceUnloaded]);

  // Send chart data when iframe is ready and we have data
  useEffect(() => {
    if (isIframeReady && libraryConfig && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: 'RENDER_CHART',
          chartId: chartIdRef.current,
          chartData: libraryConfig,
        },
        getPostMessageOrigin()
      );
    }
  }, [isIframeReady, libraryConfig]);

  return (
    <iframe
      title="Plotly Chart"
      ref={iframeRef}
      src={iframeURL}
      sandbox="allow-scripts allow-same-origin"
      referrerPolicy="no-referrer"
      className={css(styles.container)}
      {...getTestProps({component: 'PlotlyChart', element: 'root'})}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
});
